import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export type DeclineReason =
  | "too_far"
  | "not_enough_pay"
  | "too_many_stops"
  | "bad_timing"
  | "not_interested"
  | "other";

const DECLINE_REASON_OPTIONS: { value: DeclineReason; label: string }[] = [
  { value: "too_far", label: "Too far from me" },
  { value: "not_enough_pay", label: "Pay isn't worth it" },
  { value: "too_many_stops", label: "Too many stops" },
  { value: "bad_timing", label: "Bad timing right now" },
  { value: "not_interested", label: "Not interested" },
  { value: "other", label: "Something else" },
];

interface DeclineDeliveryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryRequestId: string | null;
  onDeclined?: () => void;
}

export function DeclineDeliveryDialog({
  open,
  onOpenChange,
  deliveryRequestId,
  onDeclined,
}: DeclineDeliveryDialogProps) {
  const { toast } = useToast();
  const [reason, setReason] = useState<DeclineReason>("not_interested");
  const [note, setNote] = useState("");

  const declineMutation = useMutation({
    mutationFn: async (vars: {
      deliveryRequestId: string;
      reason: DeclineReason;
      note?: string;
    }) => {
      await apiRequest("POST", `/api/delivery-requests/${vars.deliveryRequestId}/decline`, {
        reason: vars.reason,
        note: vars.note?.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast({ title: "Declined", description: "We won't show this one again." });
      onDeclined?.();
      onOpenChange(false);
      setReason("not_interested");
      setNote("");
    },
    onError: (err: any) => {
      toast({
        title: "Could not decline",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const submitDisabled =
    declineMutation.isPending || (reason === "other" && note.trim().length === 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Decline this delivery offer?</DialogTitle>
          <DialogDescription>
            We'll stop showing it to you. A reason helps us send better offers next time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Select value={reason} onValueChange={(value) => setReason(value as DeclineReason)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DECLINE_REASON_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Textarea
            placeholder={
              reason === "other" ? "Tell us what's going on (required)" : "Optional note"
            }
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={500}
            rows={3}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={submitDisabled || !deliveryRequestId}
            onClick={() => {
              if (!deliveryRequestId) return;
              declineMutation.mutate({ deliveryRequestId, reason, note });
            }}
          >
            {declineMutation.isPending ? "Declining…" : "Decline"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
